import EventNames from '../../config/eventNames';
import { getContainers } from '../../infra/startup';
import {
  AccessControlInputType,
  SetServiceDependencies
} from '../../infra/startup/types';
import IEventService from '../eventService/IEventService';
import bindAllMethods from '../../utils/bindAllMethods';
import { PromiseHelper } from '../../utils/PromiseHelper';
import { Refreshable } from '../../utils/refreshable/IRefreshable';
import { IContainer } from '../../core/containers';
import {
  AccessControlOptionsType,
  RefreshType
} from '../../interface/v1/accessControl/types';
import { Scope } from '../scope';
import userContextEnumToAuthContextEnum from '../authTokenService/utils/userContextEnumToAuthContextEnum';
import { IScopeService } from '../scope/IScopeService';
import { IActionService } from '../action/types';
import IApplicationService from '../applicationService/IApplicationService';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import { UserContextEnum } from '../../interface/types';
import { IAccessControlService } from './IAccessControlService';

export class AccessControlService
  extends Refreshable
  implements IAccessControlService
{
  private defaultKey = 'default';
  private initializedUserContexts: Map<
    UserContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private promiseHelper = new PromiseHelper();

  private _eventsService: IEventService;
  private _accessControlContainer: IContainer;
  private _scopeService: IScopeService;
  private _actionService: IActionService;
  private _applicationService: IApplicationService;
  private _enabled: boolean;
  private _manifestActions: string[];
  private _actionsPrefix: string;

  constructor(accessControl?: AccessControlInputType) {
    super();

    const {
      enabled,
      actions: manifestActions,
      actionsPrefix
    } = accessControl || {};

    this._enabled = enabled;
    this._manifestActions = manifestActions || [];
    this._actionsPrefix = actionsPrefix;

    bindAllMethods(this);

    this.setRefreshMethods({
      init: this.init.bind(this),
      refresh: this.refresh.bind(this)
    });
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    const containers = getContainers();

    this._accessControlContainer = containers.accessControlContainer;
    this._eventsService = services.eventService;
    this._scopeService = services.scopeService;
    this._actionService = services.actionService;
    this._applicationService = services.applicationService;

    // TODO: Remove this - old schema
    this._accessControlContainer.addInitServices(
      this.init,
      undefined,
      'AccessControlService'
    );
    this._accessControlContainer.addRefreshServices(
      this.refresh,
      undefined,
      'AccessControlService'
    );

    this._markAsReady();
  }

  private async refreshPromise(
    options?: AccessControlOptionsType
  ): Promise<boolean> {
    const policiesChanged = this.isProviderOneCloud()
      ? await this._actionService.refresh(
          userContextEnumToAuthContextEnum(options?.userContext)
        )
      : await this._scopeService.updateScopes(options);

    if (!policiesChanged) return false;

    void this._eventsService.publish(
      EventNames.shellAccessControlChangedEventName,
      undefined
    );

    return true;
  }

  async refresh(
    refreshOptions?: RefreshType,
    options?: AccessControlOptionsType
  ): Promise<void> {
    const promiseKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.isEnabled() && promise === undefined) {
      promise = this.refreshPromise(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }

    if (promise !== undefined) {
      await promise;
    }
  }

  /**
   * During initialization, if AuthProvider is OneCloud, uses the initial
   * policies (actions) from the manifest.
   * If AuthZ, refreshes all the policies (scopes) from the API.
   */
  async init(options?: AccessControlOptionsType): Promise<void> {
    const contextKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedUserContexts.get(contextKey);

    if (!isInitialized) {
      this.initializedUserContexts.set(contextKey, true);
      if (promise === undefined) {
        promise = this.isProviderOneCloud()
          ? this._actionService.addIfNew(
              userContextEnumToAuthContextEnum(options?.userContext),
              this.addPrefix(this._manifestActions, this._actionsPrefix)
            )
          : this.refresh({ reloadContent: false }, options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    if (promise !== undefined) {
      await promise;
    }
  }

  /**
   * @deprecated Use `check` instead.
   */
  async checkScopes(
    scopesToCheck: Scope[],
    options: AccessControlOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this.init(options);

    if (this.isProviderOneCloud()) {
      const LEGACY_ACTION_PREFIX = 'legacy/stratus.sc/';

      const scopeNames = scopesToCheck.map((scope) => scope?.scope);
      const actions = this.addPrefix(scopeNames, LEGACY_ACTION_PREFIX);
      const authContext = userContextEnumToAuthContextEnum(
        options?.userContext
      );

      return this._actionService.check(authContext, actions);
    }

    return this._scopeService.checkScopes(scopesToCheck, options);
  }

  async check(
    policies: string[],
    options: AccessControlOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this.init(options);

    if (this.isProviderOneCloud()) {
      const authContext = userContextEnumToAuthContextEnum(
        options?.userContext
      );
      return this._actionService.check(
        authContext,
        this.addPrefix(policies, this._actionsPrefix)
      );
    }

    return this._scopeService.checkScopes(
      this._scopeService.convertToScopes(policies),
      options
    );
  }

  isEnabled(): boolean {
    if (!this._enabled) {
      console.warn('AccessControl: disabled');
      return false;
    }
    return true;
  }

  private isProviderOneCloud(): boolean {
    return (
      this._applicationService.getAuthenticationProvider() ===
      AuthenticationProviderEnum.coptor
    );
  }

  private addPrefix(policies: string[], prefix?: string): string[] {
    return policies.map((policy) => {
      if (prefix && !policy.startsWith(prefix)) {
        return `${prefix}${policy}`;
      }

      return policy;
    });
  }
}
